import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, Settings, Users, FileText, Award } from 'lucide-react';
import { toast } from 'react-toastify';
import { SF_UI } from './ui/tokens';
import ConnectWalletButton from './wallet/ConnectWalletButton';
import { useWeb3AuthOptional } from '../web3/Web3AuthContext';

type NavSubItem = {
  text: string;
  path: string;
  disabled?: boolean;
  external?: boolean;
  isNew?: boolean;
  isSoon?: boolean;
};

type NavItem = {
  text: string;
  path?: string;
  disabled?: boolean;
  badge?: string;
  hasNew?: boolean;
  items?: NavSubItem[];
};

const navItems: NavItem[] = [
  { text: 'Home', path: '/' },
  {
    text: 'About Us',
    items: [
      { text: 'About Us', path: '/about' },
      { text: 'News', path: '/news' },
      { text: 'Tokenization', path: '/token' },
      { text: 'AlchemyPay', path: 'https://alchemypay.org/', external: true, isNew: true },
    ],
  },
  {
    text: 'How It Works',
    items: [
      { text: 'Overview', path: '/how-it-works' },
      { text: 'Education', path: '/education' },
      { text: 'Apply Now', path: '/apply' },
      { text: 'Funding', path: '/funding' },
    ],
  },
  
  {
    text: 'Legacy',
    hasNew: true,
    items: [
      { text: 'Overview', path: '/legacy', isNew: true },
      { text: 'Tokenized', path: '#', disabled: true },
      { text: 'Future', path: '#', disabled: true },
    ],
  },
  
  {
    text: 'Community',
    items: [
      { text: 'Community Hub', path: 'https://www.youtube.com/@SportsFutureio', external: true, isNew: true },
      { text: 'B.A.A.S.', path: '/baas' },
      { text: 'Sports Offices', path: '/sports-offices' },
      { text: 'Retail Plaza', path: 'https://apertumsportsfuture-shop.fourthwall.com/', external: true, isNew: true },
    ],
  },
  {
    text: 'Support',
    items: [
      { text: 'FAQ', path: '/faq' },
      { text: 'Legal', path: '/legal' },
      { text: 'Contact', path: '/contact' },
    ],
  },
];

type UserRole = 'admin' | 'athlete' | 'sports_agent' | 'sports_officer' | null;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const web3 = useWeb3AuthOptional();
  const user = web3?.isConnected ? web3.address : null;
  const userRole: UserRole = web3?.isAuthenticated && web3?.isAdmin ? 'admin' : null;
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const profileRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const logoutButtonRef = useRef<HTMLButtonElement>(null);
  const isLoggingOut = useRef<boolean>(false);
  const isLogoutClicked = useRef<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip if clicking logout button
      if (logoutButtonRef.current?.contains(event.target as Node)) {
        return;
      }

      // Handle dropdown menus
      if (openDropdown) {
        const dropdownRef = dropdownRefs.current[openDropdown];
        if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
          // Check if clicking on the toggle button
          const toggleButton = document.querySelector(`[data-dropdown-toggle="${openDropdown}"]`);
          if (!toggleButton?.contains(event.target as Node)) {
            setOpenDropdown(null);
          }
        }
      }

      // Handle profile menu
      if (isProfileOpen) {
        if (
          profileRef.current &&
          !profileRef.current.contains(event.target as Node) &&
          profileButtonRef.current &&
          !profileButtonRef.current.contains(event.target as Node)
        ) {
          setIsProfileOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown, isProfileOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownClick = (text: string, e: React.MouseEvent, isMobile = false) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === text ? null : text);
  };

  const setDropdownRef = (el: HTMLDivElement | null, key: string) => {
    dropdownRefs.current[key] = el;
  };

  const toggleProfileMenu = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleProfileNavigation = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    setIsProfileOpen(false);
    navigate(path);
  };

  const handleLogout = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple logout attempts
    if (isLoggingOut.current || isLogoutClicked.current) {
      console.log('Logout already in progress, ignoring duplicate event');
      return;
    }

    isLogoutClicked.current = true;
    isLoggingOut.current = true;
    setIsProfileOpen(false);

    const loadingToast = toast.loading('Signing out...');

    try {
      await web3?.signOut();
      toast.update(loadingToast, {
        render: 'Wallet disconnected',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.update(loadingToast, {
        render: 'Sign out failed, please refresh the page',
        type: 'warning',
        isLoading: false,
        autoClose: 3000,
      });
      navigate('/', { replace: true });
    } finally {
      // Reset the logging out flag
      isLoggingOut.current = false;
      isLogoutClicked.current = false;
      console.info('Logout process completed');
    }
  };

  const renderDropdownMenu = (items: { text: string; path?: string; disabled?: boolean; external?: boolean; isNew?: boolean; isSoon?: boolean }[], parentText: string) => (
    <div
      ref={(el) => setDropdownRef(el, parentText)}
      className="absolute top-full left-0 mt-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 py-2 min-w-[200px] z-[40000]"
    >
      {items.map((item) =>
        item.disabled ? (
          <span key={item.text} className="flex items-center justify-between px-4 py-2 text-gray-400 cursor-not-allowed">
            <span>{item.text}</span>
            <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
              soon
            </span>
          </span>
        ) : item.external ? (
          <a
            key={item.text}
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={() => {
              setOpenDropdown(null);
              setIsMenuOpen(false);
            }}
          >
            <span>{item.text}</span>
            {item.isSoon && (
              <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                soon
              </span>
            )}
            {item.isNew && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                New
              </span>
            )}
          </a>
        ) : (
          <NavLink
            key={item.text}
            to={item.path!}
            className={({ isActive }) =>
              `flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700 ${isActive ? 'text-primary' : ''}`
            }
            onClick={() => {
              setOpenDropdown(null);
              setIsMenuOpen(false);
            }}
          >
            <span>{item.text}</span>
            {item.isSoon && (
              <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                soon
              </span>
            )}
            {item.isNew && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                New
              </span>
            )}
          </NavLink>
        )
      )}
    </div>
  );

  const renderProfileMenu = () => (
    <div
      ref={profileRef}
      className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 py-2 min-w-[200px] z-[40000]"
    >
      {userRole === 'admin' && (
        <>
          <NavLink
            to="/admin/dashboard"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/admin/dashboard')}
          >
            <Settings size={16} className="mr-2" />
            Admin Dashboard
          </NavLink>
          <NavLink
            to="/admin/applications"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/admin/applications')}
          >
            <FileText size={16} className="mr-2" />
            Review Applications
          </NavLink>
          <div className="border-t border-gray-200 my-1"></div>
        </>
      )}

      {userRole === 'athlete' && (
        <>
          <NavLink
            to="/athlete/profile"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/athlete/profile')}
          >
            <User size={16} className="mr-2" />
            My Profile
          </NavLink>
          <NavLink
            to="/athlete/achievements"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/athlete/achievements')}
          >
            <Award size={16} className="mr-2" />
            Achievements
          </NavLink>
          <div className="border-t border-gray-200 my-1"></div>
        </>
      )}

      {userRole === 'sports_agent' && (
        <>
          <NavLink
            to="/agent/dashboard"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/agent/dashboard')}
          >
            <Users size={16} className="mr-2" />
            My Athletes
          </NavLink>
          <div className="border-t border-gray-200 my-1"></div>
        </>
      )}

      {userRole === 'sports_officer' && (
        <>
          <NavLink
            to="/officer/dashboard"
            className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700"
            onClick={(e) => handleProfileNavigation(e, '/officer/dashboard')}
          >
            <Users size={16} className="mr-2" />
            My Region
          </NavLink>
          <div className="border-t border-gray-200 my-1"></div>
        </>
      )}

      <button
        ref={logoutButtonRef}
        onClick={handleLogout}
        onTouchStart={handleLogout}
        className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100:bg-gray-700 cursor-pointer"
        aria-label="Sign Out"
        style={{ touchAction: 'manipulation' }}
      >
        <LogOut size={16} className="mr-2" />
        <span>Sign Out</span>
      </button>
    </div>
  );

  return (
    <header
      ref={headerRef}
      data-sf-ui="chrome-header"
      className={`${SF_UI.systemId} ${SF_UI.headerShell} fixed inset-x-0 top-0 z-[40000] max-w-[100vw] transition-all duration-300 ${
        scrolled ? 'shadow-md py-2' : 'py-3'
      }`}
    >
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-4">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <NavLink to="/" className="flex min-w-0 items-center" aria-label="SportsFuture home">
            <img
              src="/SFUT_icon_small.png"
              alt=""
              className="h-9 w-9 flex-shrink-0 sm:h-11 sm:w-11 lg:h-12 lg:w-12"
            />
            <span className="ml-1.5 whitespace-nowrap text-[15px] font-bold tracking-tight text-primary sm:ml-2 sm:text-lg lg:text-xl">
              SportsFuture
            </span>
          </NavLink>

          {/* Centered nav links — lg+ only; tablets keep the hamburger so items don't overflow */}
          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-3 xl:gap-6 lg:flex">
            {navItems.map((item) => (
              <div key={item.text} className="relative group h-full flex items-center">
                {'disabled' in item && item.disabled ? (
                  /* Disabled nav item (e.g. Legacy - Coming Soon) */
                  <span
                    className="h-full flex items-center whitespace-nowrap font-medium text-gray-400 cursor-not-allowed select-none"
                  >
                    {item.text}
                    {'badge' in item && item.badge && (
                      <span className="ml-1.5 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                        {item.badge}
                      </span>
                    )}
                  </span>
                ) : 'items' in item ? (
                  <div className="relative h-full flex items-center">
                    <button
                      data-dropdown-toggle={item.text}
                      className={`h-full flex items-center whitespace-nowrap font-medium transition-colors hover:text-primary:text-white group-hover:text-primary ${
                        openDropdown === item.text ? 'text-primary' : 'text-gray-700'
                      }`}
                      onClick={(e) => handleDropdownClick(item.text, e)}
                    >
                      {item.text}
                      {'hasNew' in item && item.hasNew && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                          New
                        </span>
                      )}
                      <ChevronDown size={16} className="ml-1" />
                    </button>
                    {openDropdown === item.text && renderDropdownMenu(item.items, item.text)}
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `h-full flex items-center whitespace-nowrap font-medium transition-colors hover:text-primary:text-white ${
                        isActive ? 'text-primary border-b-2 border-primary' : 'text-gray-700'
                      }`
                    }
                  >
                    {item.text}
                  </NavLink>
                )}
              </div>
            ))}
          </nav>

          {/* Right-side controls (desktop) */}
          <div className="hidden flex-shrink-0 items-center gap-3 lg:flex">
            <ConnectWalletButton />
            {userRole === 'admin' && (
              <div className="relative">
                <button
                  ref={profileButtonRef}
                  onClick={toggleProfileMenu}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white hover:bg-primary-600 transition-colors"
                  aria-label="Open admin menu"
                >
                  <User size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Phone + tablet controls — hamburger is last and never shrinks */}
          <div className="flex min-w-0 flex-shrink-0 items-center gap-2 lg:hidden">
            <div className="min-w-0 max-w-[min(9.5rem,calc(100vw-6.5rem))] sm:max-w-none">
              <ConnectWalletButton />
            </div>

            <button
              type="button"
              data-sf="nav-toggle"
              onClick={toggleMenu}
              className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-slate-100 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="sf-mobile-nav"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div
            id="sf-mobile-nav"
            className="mt-2 max-h-[min(70dvh,calc(100dvh-5.5rem))] overflow-y-auto overscroll-contain py-2 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 animate-fade-in lg:hidden"
          >
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) =>
                'disabled' in item && item.disabled ? (
                  /* Disabled mobile nav item (e.g. Legacy - Coming Soon) */
                  <span
                    key={item.text}
                    className="py-2 px-4 font-medium text-gray-400 cursor-not-allowed select-none flex items-center justify-between"
                  >
                    <span>{item.text}</span>
                    {'badge' in item && item.badge && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                        {item.badge}
                      </span>
                    )}
                  </span>
                ) : 'items' in item ? (
                  <div key={item.text}>
                    <button
                      data-dropdown-toggle={item.text}
                      className={`w-full text-left py-2 px-4 font-medium transition-colors flex items-center justify-between ${
                        openDropdown === item.text ? 'text-primary bg-primary-50' : 'text-gray-700 hover:bg-gray-100:bg-gray-800'
                      }`}
                      onClick={(e) => handleDropdownClick(item.text, e, true)}
                    >
                      <span className="flex items-center">
                        {item.text}
                        {'hasNew' in item && item.hasNew && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                            New
                          </span>
                        )}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`transform transition-transform ${openDropdown === item.text ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {openDropdown === item.text && (
                      <div ref={(el) => setDropdownRef(el, item.text)} className="bg-gray-50 py-2">
                        {item.items.map((subItem) =>
                          subItem.disabled ? (
                            <span
                              key={subItem.text}
                              className="flex items-center justify-between py-2 px-8 font-medium text-gray-400 cursor-not-allowed"
                            >
                              <span>{subItem.text}</span>
                              <span className="px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                                soon
                              </span>
                            </span>
                          ) : subItem.external ? (
                            <a
                              key={subItem.text}
                              href={subItem.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between py-2 px-8 font-medium text-gray-700 hover:bg-gray-100:bg-gray-700 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTimeout(() => {
                                  setOpenDropdown(null);
                                  setIsMenuOpen(false);
                                }, 100);
                              }}
                            >
                              <span>{subItem.text}</span>
                              {subItem.isSoon && (
                                <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                                  soon
                                </span>
                              )}
                              {subItem.isNew && (
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                                  New
                                </span>
                              )}
                            </a>
                          ) : (
                            <NavLink
                              key={subItem.text}
                              to={subItem.path!}
                              className={({ isActive }) =>
                                `flex items-center justify-between py-2 px-8 font-medium transition-colors ${
                                  isActive ? 'text-primary bg-primary-50' : 'text-gray-700 hover:bg-gray-100:bg-gray-700'
                                }`
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setTimeout(() => {
                                  setOpenDropdown(null);
                                  setIsMenuOpen(false);
                                }, 100);
                              }}
                            >
                              <span>{subItem.text}</span>
                              {subItem.isSoon && (
                                <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide">
                                  soon
                                </span>
                              )}
                              {subItem.isNew && (
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase">
                                  New
                                </span>
                              )}
                            </NavLink>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `py-2 px-4 font-medium transition-colors ${
                        isActive ? 'text-primary bg-primary-50' : 'text-gray-700 hover:bg-gray-100:bg-gray-800'
                      }`
                    }
                  >
                    {item.text}
                  </NavLink>
                )
              )}

              {userRole === 'admin' && (
                <NavLink
                  to="/admin/dashboard"
                  onClick={() => setIsMenuOpen(false)}
                  className="py-2 px-4 font-medium text-primary hover:bg-gray-100"
                >
                  Admin
                </NavLink>
              )}
            </nav>
          </div>
        )}

        {isProfileOpen && (user || userRole === 'admin') && (
          <div className="absolute top-full right-0 mt-4 z-[40000]">
            {renderProfileMenu()}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
